import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaRocket, FaChartLine, FaBalanceScale, FaFileInvoiceDollar, FaGlobe, FaHandshake } from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import styled, { keyframes } from "styled-components";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(50px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ServiceCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  margin-bottom: 30px;
  animation: ${fadeUp} 0.5s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};
  height: 100%;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
`;

const ServiceIcon = styled.div`
  font-size: 42px;
  color: ${props => props.color || "#0d6efd"};
  margin-bottom: 20px;
`;

const ServiceTitle = styled.h4`
  font-weight: 700;
  margin-bottom: 15px;
  color: #333;
`;

const ServiceDescription = styled.p`
  color: #666;
  font-size: 16px;
  line-height: 1.6;
`;

const Section = styled.section`
  padding: 80px 0;
  background: ${props => props.background || "white"};
`;

const ServiceListItem = styled.div`
  display: flex;
  margin-bottom: 15px;
  opacity: 0;
  transform: translateY(30px);
  animation: ${fadeUp} 0.6s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};
`;

const ServiceListIcon = styled.div`
  margin-right: 15px;
  color: #0d6efd;
  flex-shrink: 0;
  margin-top: 3px;
`;

const ServiceListContent = styled.div`
  flex-grow: 1;
`;

const ServiceListTitle = styled.h5`
  font-weight: 700;
  margin-bottom: 5px;
  color: #333;
`;

const ServiceListDescription = styled.p`
  color: #666;
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 0;
`;

const Startup = () => {
    const [setIsVisible] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const faqSection = document.getElementById("faq-section");
            if (faqSection) {
                const rect = faqSection.getBoundingClientRect();
                if (rect.top < window.innerHeight * 0.8 && rect.bottom > 0) {
                    setIsVisible(true);
                } else {
                    setIsVisible(false);
                }
            }
        };

        window.addEventListener("scroll", handleScroll);
        handleScroll();
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const servicesData = [
        {
            icon: <FaRocket />,
            title: "Création et Structuration",
            description: "Conseil sur le choix de la forme juridique, rédaction des statuts, business plan et formalités de création pour lancer votre startup.",
            color: "#0d6efd"
        },
        {
            icon: <FaFileInvoiceDollar />,
            title: "Comptabilité et Gestion Administrative",
            description: "Tenue de comptabilité, déclarations fiscales et sociales, gestion de la paie et tableaux de bord personnalisés.",
            color: "#198754"
        },
        {
            icon: <FaChartLine />,
            title: "Pilotage de la Performance",
            description: "Contrôle de gestion, budget prévisionnel, analyse financière et conseil en stratégie de croissance pour optimiser vos performances.",
            color: "#dc3545"
        },
        {
            icon: <FaHandshake />,
            title: "Levée de Fonds et Relations Investisseurs",
            description: "Préparation du pitch deck, accompagnement dans la recherche d'investisseurs, due diligence et reporting investisseurs.",
            color: "#6610f2"
        }
    ];

    const serviceDetails = {
        creation: [
            {
                title: "Conseil sur le choix de la forme juridique",
                description: "SAS, SASU, SARL... Nous vous aidons à choisir la structure la plus adaptée à votre projet et à vos objectifs."
            },
            {
                title: "Rédaction des statuts",
                description: "Nous rédigeons des statuts sur mesure (en partenariat avec un office notarial), optimisés pour votre activité et stratégie."
            },
            {
                title: "Business Plan",
                description: "Construction de votre business plan avec projections financières réalistes pour convaincre investisseurs et banques."
            },
            {
                title: "Formalités de création",
                description: "Prise en charge de toutes les démarches administratives pour immatriculer votre entreprise."
            },
            {
                title: "Conseil en financement initial",
                description: "Identification des sources de financement adaptées à votre projet et préparation des dossiers de demande."
            }
        ],
        comptabilite: [
            {
                title: "Tenue de comptabilité",
                description: "Gestion complète de votre comptabilité avec des outils digitaux pour une visibilité en temps réel de votre situation financière."
            },
            {
                title: "Déclarations fiscales et sociales",
                description: "Établissement et télétransmission de toutes vos déclarations fiscales et sociales avec optimisation de votre fiscalité."
            },
            {
                title: "Gestion de la paie",
                description: "Prise en charge intégrale de la gestion de la paie de vos salariés et conseil sur les dispositifs d'intéressement."
            },
            {
                title: "Gestion administrative",
                description: "Organisation de votre gestion administrative (facturation, suivi des encaissements, gestion des notes de frais, etc.)."
            },
            {
                title: "Tableaux de bord personnalisés",
                description: "Mise en place de tableaux de bord avec les KPI pertinents pour suivre l'évolution de votre activité en temps réel."
            }
        ],
        performance: [
            {
                title: "Contrôle de gestion",
                description: "Mise en place d'outils de contrôle de gestion pour piloter votre rentabilité et optimiser vos coûts."
            },
            {
                title: "Budget prévisionnel",
                description: "Élaboration de budgets prévisionnels pour anticiper vos besoins de financement et suivre l'évolution de votre activité."
            },
            {
                title: "Analyse financière",
                description: "Analyse de vos états financiers pour identifier vos forces, faiblesses et axes d'amélioration."
            },
            {
                title: "Conseil en stratégie de croissance",
                description: "Accompagnement dans la définition et mise en œuvre de votre stratégie de développement."
            },
            {
                title: "Évaluation d'entreprise",
                description: "Réalisation d'évaluations de votre entreprise dans le cadre de levées de fonds ou de cessions."
            }
        ],
        levee: [
            {
                title: "Préparation du pitch deck",
                description: "Aide à la création d'un pitch deck percutant pour présenter efficacement votre projet aux investisseurs."
            },
            {
                title: "Accompagnement dans la recherche d'investisseurs",
                description: "Mise en relation avec notre réseau d'investisseurs et accompagnement dans les négociations."
            },
            {
                title: "Due diligence",
                description: "Préparation à la due diligence financière et assistance lors de ce processus crucial."
            },
            {
                title: "Reporting investisseurs",
                description: "Mise en place d'un reporting régulier et transparent pour maintenir la confiance de vos investisseurs."
            }
        ],
        juridique: [
            {
                title: "Conseil juridique",
                description: "Conseils sur les aspects juridiques de votre activité en partenariat avec des avocats spécialisés."
            },
            {
                title: "Optimisation fiscale",
                description: "Optimisation de votre fiscalité, choix des régimes fiscaux et accompagnement pour les crédits d'impôt."
            },
            {
                title: "Gestion des stock-options et BSPCE",
                description: "Accompagnement dans la mise en place et gestion de plans de stock-options pour motiver vos équipes."
            }
        ],
        international: [
            {
                title: "Stratégie d'expansion internationale",
                description: "Conseil et accompagnement pour votre développement à l'étranger et votre stratégie d'expansion."
            },
            {
                title: "Fiscalité internationale",
                description: "Expertise en matière de fiscalité internationale pour optimiser votre implantation à l'étranger."
            },
            {
                title: "Conformité réglementaire internationale",
                description: "Accompagnement pour assurer votre conformité avec les réglementations des différents pays où vous opérez."
            }
        ]
    };

    return (
        <>
            <Header />

            <section className="service-banner d-flex align-items-center">
                <div className="container-fluid px-4 px-md-5 text-white">
                    <div className="row">
                        <div className="col-lg-12">
                            <h1 className="fw-bold display-4 mb-3">Expertise Comptable et Conseil pour Startups Innovantes</h1>
                            <p className="fs-5 mb-4">Votre Partenaire Stratégique de la Création à la Croissance</p>
                            <nav aria-label="breadcrumb">

                            </nav>
                        </div>
                    </div>
                </div>
            </section>

            <Section>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row">
                        <div className="col-12">
                            <h2 className="fw-bold custom-title text-center mb-5">
                                CINEF-EC comprend les défis uniques des startups innovantes
                            </h2>
                            <p className="text-muted text-center fs-5 mb-5">
                                Vous avez besoin de bien plus qu'un simple comptable : vous avez besoin d'un partenaire stratégique 
                                qui vous accompagne à chaque étape de votre croissance, de l'idée initiale à l'expansion internationale. 
                                Nous sommes spécialisés dans l'accompagnement des startups et des entreprises innovantes, et nous mettons 
                                à votre disposition une expertise complète et personnalisée pour vous aider à réussir.
                            </p>
                        </div>
                    </div>

                    <div className="row align-items-center">
                        <div className="col-lg-12">
                            <h3 className="fw-bold custom-title mb-4">Pourquoi choisir CINEF-EC pour votre startup ?</h3>
                            <p className="mb-4">
                                Notre cabinet dispose d'une expertise particulière dans l'accompagnement des startups.
                                Nous comprenons les enjeux spécifiques de votre entreprise innovante : levées de fonds, business models innovants, 
                                croissance rapide, enjeux de scalabilité, stock-options, etc.
                            </p>
                            <p className="mb-4">
                                Notre objectif est de vous accompagner dans vos obligations légales et réglementaires
                                tout en vous aidant à développer votre entreprise et à optimiser sa gestion.
                            </p>
                            <div className="d-flex mt-4">
                                <div className="d-flex align-items-center me-4">
                                    <div className="rounded-circle bg-primary d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                                        <FaRocket className="text-white fs-4" />
                                    </div>
                                    <span className="ms-2 fw-bold">Expertise dédiée</span>
                                </div>
                                <div className="d-flex align-items-center me-4">
                                    <div className="rounded-circle bg-success d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                                        <FaChartLine className="text-white fs-4" />
                                    </div>
                                    <span className="ms-2 fw-bold">Conseil proactif</span>
                                </div>
                                <div className="d-flex align-items-center">
                                    <div className="rounded-circle bg-danger d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                                        <FaGlobe className="text-white fs-4" />
                                    </div>
                                    <span className="ms-2 fw-bold">Réseau et connexions</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Section>

            <Section background="#f8f9fa">
                <div className="container-fluid px-4 px-md-5">
                    <div className="row mb-5">
                        <div className="col-12 text-center">
                            <h2 className="fw-bold custom-title text-center mb-4">Nos Services pour les Startups</h2>
                            <p className="text-muted fs-5 mb-5">Une offre complète et modulable pour couvrir tous vos besoins</p>
                        </div>
                    </div>
                    
                    <div className="row">
                        {servicesData.map((service, index) => (
                            <div className="col-md-6 col-lg-3 mb-4" key={index}>
                                <ServiceCard delay={`${index * 0.1}s`}>
                                    <ServiceIcon color={service.color}>
                                        {service.icon}
                                    </ServiceIcon>
                                    <ServiceTitle>{service.title}</ServiceTitle>
                                    <ServiceDescription>{service.description}</ServiceDescription>
                                </ServiceCard>
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            <Section>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row mb-5">
                        <div className="col-12">
                            <h3 className="fw-bold custom-title mb-4">1. Création et Structuration</h3>
                        </div>
                    </div>

                    <div className="row mb-5">
                        {serviceDetails.creation.map((item, index) => (
                            <div className="col-md-6 mb-4" key={`creation-${index}`}>
                                <ServiceListItem delay={`${index * 0.1}s`}>
                                    <ServiceListIcon>
                                        <i className="fas fa-check-circle fa-lg"></i>
                                    </ServiceListIcon>
                                    <ServiceListContent>
                                        <ServiceListTitle>{item.title}</ServiceListTitle>
                                        <ServiceListDescription>{item.description}</ServiceListDescription>
                                    </ServiceListContent>
                                </ServiceListItem>
                            </div>
                        ))}
                    </div>

                    <div className="row mb-5">
                        <div className="col-12">
                            <h3 className="fw-bold custom-title mb-4">2. Comptabilité et Gestion Administrative</h3>
                        </div>
                    </div>

                    <div className="row mb-5">
                        {serviceDetails.comptabilite.map((item, index) => (
                            <div className="col-md-6 mb-4" key={`comptabilite-${index}`}>
                                <ServiceListItem delay={`${index * 0.1}s`}>
                                    <ServiceListIcon>
                                        <i className="fas fa-check-circle fa-lg"></i>
                                    </ServiceListIcon>
                                    <ServiceListContent>
                                        <ServiceListTitle>{item.title}</ServiceListTitle>
                                        <ServiceListDescription>{item.description}</ServiceListDescription>
                                    </ServiceListContent>
                                </ServiceListItem>
                            </div>
                        ))}
                    </div>

                    <div className="row mb-5">
                        <div className="col-12">
                            <h3 className="fw-bold custom-title mb-4">3. Pilotage de la Performance et Conseil Stratégique</h3>
                        </div>
                    </div>

                    <div className="row mb-5">
                        {serviceDetails.performance.map((item, index) => (
                            <div className="col-md-6 mb-4" key={`performance-${index}`}>
                                <ServiceListItem delay={`${index * 0.1}s`}>
                                    <ServiceListIcon>
                                        <i className="fas fa-check-circle fa-lg"></i>
                                    </ServiceListIcon>
                                    <ServiceListContent>
                                        <ServiceListTitle>{item.title}</ServiceListTitle>
                                        <ServiceListDescription>{item.description}</ServiceListDescription>
                                    </ServiceListContent>
                                </ServiceListItem>
                            </div>
                        ))}
                    </div>

                    <div className="row mb-5">
                        <div className="col-12">
                            <h3 className="fw-bold custom-title mb-4">4. Levée de Fonds et Relations Investisseurs</h3>
                        </div>
                    </div>

                    <div className="row mb-5">
                        {serviceDetails.levee.map((item, index) => (
                            <div className="col-md-6 mb-4" key={`levee-${index}`}>
                                <ServiceListItem delay={`${index * 0.1}s`}>
                                    <ServiceListIcon>
                                        <i className="fas fa-check-circle fa-lg"></i>
                                    </ServiceListIcon>
                                    <ServiceListContent>
                                        <ServiceListTitle>{item.title}</ServiceListTitle>
                                        <ServiceListDescription>{item.description}</ServiceListDescription>
                                    </ServiceListContent>
                                </ServiceListItem>
                            </div>
                        ))}
                    </div>

                    <div className="row mb-5">
                        <div className="col-12">
                            <h3 className="fw-bold custom-title mb-4">5. Juridique et Fiscal</h3>
                        </div>
                    </div>

                    <div className="row mb-5">
                        {serviceDetails.juridique.map((item, index) => (
                            <div className="col-md-6 mb-4" key={`juridique-${index}`}>
                                <ServiceListItem delay={`${index * 0.1}s`}>
                                    <ServiceListIcon>
                                        <i className="fas fa-check-circle fa-lg"></i>
                                    </ServiceListIcon>
                                    <ServiceListContent>
                                        <ServiceListTitle>{item.title}</ServiceListTitle>
                                        <ServiceListDescription>{item.description}</ServiceListDescription>
                                    </ServiceListContent>
                                </ServiceListItem>
                            </div>
                        ))}
                    </div>

                    <div className="row mb-5">
                        <div className="col-12">
                            <h3 className="fw-bold custom-title mb-4">6. Internationalisation</h3>
                        </div>
                    </div>

                    <div className="row mb-5">
                        {serviceDetails.international.map((item, index) => (
                            <div className="col-md-6 mb-4" key={`international-${index}`}>
                                <ServiceListItem delay={`${index * 0.1}s`}>
                                    <ServiceListIcon>
                                        <i className="fas fa-check-circle fa-lg"></i>
                                    </ServiceListIcon>
                                    <ServiceListContent>
                                        <ServiceListTitle>{item.title}</ServiceListTitle>
                                        <ServiceListDescription>{item.description}</ServiceListDescription>
                                    </ServiceListContent>
                                </ServiceListItem>
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            <Section>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row justify-content-center mb-5">
                        <div className="col-12 col-md-10 col-lg-8">
                            <div className="call-to-action p-5 rounded-3 text-center">
                                <h3 className="fw-bold text-white mb-3">Prêt à propulser votre startup vers le succès ?</h3>
                                <p className="text-white fs-5 mb-4">
                                    Contactez-nous dès aujourd'hui pour une consultation gratuite et personnalisée.
                                    Nous serions ravis de discuter de votre projet et de vous montrer comment nous pouvons vous aider à atteindre vos objectifs.
                                </p>
                                <a href="/contact" className="btn btn-light btn-lg fw-bold px-4">Demander un entretien gratuit</a>
                            </div>
                        </div>
                    </div>
                </div>
            </Section>

            <Footer />

            <style>{`
        .service-banner {
          background: linear-gradient(to right, rgba(0, 138, 68, 0.85), rgba(220, 53, 69, 0.85)), 
                      url("/assets/images/startup-banner.jpg");
          background-size: cover;
          background-position: center;
          height: 400px;
          display: flex;
          align-items: center;
        }

        .breadcrumb {
          background: transparent;
          font-size: 18px;
        }

        .breadcrumb-item a {
          color: white;
          font-weight: 500;
        }

        .custom-title {
          position: relative;
          padding-bottom: 15px;
        }

        .custom-title:after {
          content: '';
          position: absolute;
          left: 0;
          bottom: 0;
          width: 60px;
          height: 3px;
          background: linear-gradient(to right, #008a44, #dc3545);
        }

        .text-center .custom-title:after {
          left: 50%;
          transform: translateX(-50%);
        }

        .custom-underline {
          height: 4px;
          width: 70px;
          background: linear-gradient(to right, #008a44, #dc3545);
          margin-top: 10px;
        }

        .call-to-action {
          background: linear-gradient(to right, #008a44, #dc3545);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        }

        #faq-section {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s ease;
        }

        #faq-section.visible {
          opacity: 1;
          transform: translateY(0);
        }
        
        @media (max-width: 768px) {
          .service-banner {
            height: 350px;
          }
          
          .custom-title {
            font-size: 1.75rem;
          }
          
          .d-flex {
            flex-wrap: wrap;
          }
          
          .d-flex .d-flex {
            margin-bottom: 10px;
          }
        }

        .fa-check-circle {
          color: #0d6efd;
        }
      `}</style>
        </>
    );
};

export default Startup;